/**
 * Create Tenant Page
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Alert,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useCreateTenant } from '../hooks';
import { useSnackbar } from 'notistack';

// Detect environment from hostname
const getDefaultEnvironment = (): string => {
  const hostname = window.location.hostname;
  if (hostname.includes('-staging') || hostname.includes('staging.')) {
    return 'staging';
  }
  if (hostname.includes('-test') || hostname.includes('test.')) {
    return 'test';
  }
  return 'production';
};

export function CreateTenantPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const createTenant = useCreateTenant();

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    environment: getDefaultEnvironment(),
    createTestEnvironment: false,
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Auto-generate slug from name
    if (field === 'name' && typeof value === 'string') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug must only contain lowercase letters, numbers, and hyphens';
    }

    if (formData.adminEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      newErrors.adminEmail = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const adminUser =
        formData.adminFirstName && formData.adminLastName && formData.adminEmail
          ? {
              firstName: formData.adminFirstName,
              lastName: formData.adminLastName,
              email: formData.adminEmail,
              phone: formData.adminPhone || undefined,
            }
          : undefined;

      const tenant = await createTenant.mutateAsync({
        name: formData.name,
        slug: formData.slug,
        environment: formData.environment,
        createTestEnvironment: formData.createTestEnvironment,
        adminUser,
      });

      enqueueSnackbar('Tenant created successfully', { variant: 'success' });
      navigate(`/tenants/${tenant.id}`);
    } catch {
      enqueueSnackbar('Failed to create tenant', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/tenants')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight={600}>
          Create Tenant
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Tenant Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Tenant Name"
                      value={formData.name}
                      onChange={handleChange('name')}
                      error={!!errors.name}
                      helperText={errors.name}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Slug"
                      value={formData.slug}
                      onChange={handleChange('slug')}
                      error={!!errors.slug}
                      helperText={errors.slug || 'Used in URLs: tenant-slug.npsolution.no'}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel id="environment-label">Environment</InputLabel>
                      <Select
                        labelId="environment-label"
                        value={formData.environment}
                        label="Environment"
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, environment: e.target.value }))
                        }
                      >
                        <MenuItem value="production">Production</MenuItem>
                        <MenuItem value="staging">Staging</MenuItem>
                        <MenuItem value="test">Test</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.createTestEnvironment}
                      onChange={handleChange('createTestEnvironment')}
                    />
                  }
                  label="Create additional test environment"
                  sx={{ mt: 2 }}
                />

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                  Admin User (Optional)
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  If provided, an admin user will be created with OAuth access.
                  The email must match their Google or Microsoft account.
                </Alert>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={formData.adminFirstName}
                      onChange={handleChange('adminFirstName')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={formData.adminLastName}
                      onChange={handleChange('adminLastName')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.adminEmail}
                      onChange={handleChange('adminEmail')}
                      error={!!errors.adminEmail}
                      helperText={errors.adminEmail}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={formData.adminPhone}
                      onChange={handleChange('adminPhone')}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Summary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Name:</strong> {formData.name || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Slug:</strong> {formData.slug || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Environment:</strong>{' '}
                    {formData.environment.charAt(0).toUpperCase() + formData.environment.slice(1)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Additional Test Env:</strong>{' '}
                    {formData.createTestEnvironment ? 'Yes' : 'No'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Admin User:</strong>{' '}
                    {formData.adminEmail || 'Not provided'}
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={createTenant.isPending}
                >
                  {createTenant.isPending ? 'Creating...' : 'Create Tenant'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
